import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, MoreVertical } from "lucide-react";
import { ReactComponent as WifiLiveIcon } from "../../../../assets/icons/svg/wifi-live-icon.svg";
import { ReactComponent as WifiOfflineIcon } from "../../../../assets/icons/svg/wifi-offline-icon.svg";
import { ReactComponent as Filter } from "../../../../assets/icons/svg/filter-icon.svg";
import { ReactComponent as UploadScreen } from "../../../../assets/icons/svg/add-screen-image.svg";
import { ReactComponent as ScreenAdd } from "../../../../assets/icons/svg/add-screen.svg";
import { ReactComponent as All } from "../../../../assets/icons/svg/all.svg";
import { ReactComponent as Landscape } from "../../../../assets/icons/svg/landscape.svg";
import { ReactComponent as Portrait } from "../../../../assets/icons/svg/Protrait.svg";
import { ReactComponent as AddGroup } from "../../../../assets/icons/svg/add-folder.svg";
import { ReactComponent as AddTag } from "../../../../assets/icons/svg/add-tag.svg";
import { ReactComponent as PenIcon } from "../../../../assets/icons/svg/pen-icon.svg";
import { ReactComponent as ArrowRight } from "../../../../assets/icons/svg/arrow-right-gray.svg";
import { ReactComponent as ArrowDown } from "../../../../assets/icons/svg/arrow-down-gray.svg";
import { ReactComponent as Folder } from "../../../../assets/icons/svg/folder.svg";
import { useScreens } from "../../../../context/ScreenContext";
import { useGroups } from "../../../../context/GroupContext";
import { useTags, Tag, SelectedTag } from "../../../../context/TagContext";
import { useTopBar } from "../../../../context/TopBarContext";
import ToastContainer, { useToast, ToastType } from "../../../ui/Toast_Message";
import ScreenUploadPopup from "./ScreenUploadPopup";
import TagManagementPopup from "./TagManagementPopup";
import AddGroupPopup from "./AddGroupPopup";
import ScreenFilterPopup from "./ScreenFilterPopup";
import DeletePopup from "../../../ui/DeletePopup";

type SortType = "name" | "group" | "status" | "createdAt";
type SortOrder = "asc" | "desc";

interface Screen {
  id: string;
  name: string;
  group: string; // Group ID
  orientation: string;
  tags: SelectedTag[];
  status: string;
  createdAt: Date;
}

interface FilterState {
  status: string[];
  groups: string[];
  orientations: string[];
  tags: string[];
  cities: string[];
}

interface GroupFolderProps {
  group: { id: string; name: string };
  screens: Screen[];
  onDeleteGroup: (groupId: string) => void;
  handleEditScreen: (screen: Screen) => void;
  isNewlyCreated?: boolean;
}

const GroupFolder: React.FC<GroupFolderProps> = ({
  group,
  screens,
  onDeleteGroup,
  handleEditScreen,
  isNewlyCreated,
}) => {
  const [isOpen, setIsOpen] = useState(isNewlyCreated || false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  const groupScreens = screens.filter((screen) => screen.group === group.id);
  const screenCount = groupScreens.length;

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "live":
        return "bg-green-100 text-green-700";
      case "offline":
        return "bg-red-100 text-red-700";
      case "maintenance":
        return "bg-yellow-100 text-yellow-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "live":
        return <WifiLiveIcon width={12} height={12} />;
      case "offline":
        return <WifiOfflineIcon width={12} height={12} />;
      case "maintenance":
        return "⚠";
      default:
        return null;
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDropdown(false);
    setShowDeletePopup(true);
  };

  const handleConfirmDelete = () => {
    onDeleteGroup(group.id);
    setShowDeletePopup(false);
  };

  const handleCancelDelete = () => {
    setShowDeletePopup(false);
  };

  return (
    <div className="mb-4">
      {showDeletePopup && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-30 z-40" />
          <div className="fixed inset-0 flex items-center justify-center z-50">
            <DeletePopup
              title="Delete Group"
              message={`Are you sure you want to delete the group "${group.name}"?`}
              onCancel={handleCancelDelete}
              onConfirm={handleConfirmDelete}
            />
          </div>
        </>
      )}

      <div
        className={`relative z-30 transition-opacity duration-200 ${
          showDeletePopup ? "pointer-events-none opacity-30 select-none" : ""
        }`}
      >
        <div
          className={`relative bg-[#FDFDFD] rounded-[10px] px-6 py-4 shadow-sm border-[0.5px] border-[#E4E4E4] ${
            isNewlyCreated ? "border-blue-500 bg-blue-50" : ""
          }`}
        >
          <div className="flex items-center justify-between">
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? (
                <ArrowDown className="w-5 h-5 text-gray-600" />
              ) : (
                <ArrowRight className="w-5 h-5 text-gray-600" />
              )}
              {isOpen ? (
                <Folder className="w-6 h-6 text-[#000000]" />
              ) : (
                <Folder className="w-6 h-6 text-[#000000]" />
              )}
              <div>
                <h3 className="font-medium text-gray-900 text-base">
                  {group.name}
                </h3>
              </div>
              <div>
                <p className="text-sm text-[#0090AD]">
                  ({screenCount} {screenCount === 1 ? "screen" : "screens"})
                </p>
              </div>
            </div>

            <div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDropdown(!showDropdown);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
              >
                <MoreVertical className="w-4 h-4" />
              </button>

              {showDropdown && (
                <div className="absolute right-4 mt-2 z-50">
                  <div className="rounded-[10px] border-[0.5px] border-[#E4E4E4] bg-[#FDFDFD] px-4 py-2 w-[160px] shadow-md">
                    <button
                      onClick={handleDeleteClick}
                      className="w-full px-2 py-1 text-sm text-[#1F1F1F] rounded-[8] flex items-center gap-2"
                    >
                      Delete Group
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {isOpen && screenCount > 0 && (
            <div className="mt-3 ml-1 space-y-4">
              {groupScreens.map((screen) => (
                <div
                  key={screen.id}
                  className="bg-white rounded-[10px] px-6 py-3 3xl:py-5 hover:shadow-xl"
                  style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }}
                >
                  <div className="flex items-center">
                    <div className="w-12 flex-shrink-0">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: "#0BA6C71A" }}
                      >
                        {screen.orientation === "landscape" && (
                          <Landscape className="w-5 h-5 text-[#0BA6C7]" />
                        )}
                        {screen.orientation === "portrait" && (
                          <Portrait className="w-5 h-5 text-[#0BA6C7]" />
                        )}
                        {screen.orientation === "custom" && (
                          <All className="w-5 h-5 text-[#0BA6C7]" />
                        )}
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {screen.name}
                      </div>
                      <div className="text-sm text-gray-500 truncate flex items-center gap-2">
                        <span className="text-xs">{screen.id}</span>
                        <span className="text-gray-300">|</span>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                            screen.status
                          )}`}
                        >
                          {getStatusIcon(screen.status)} {screen.status}
                        </span>
                      </div>
                    </div>

                    <div className="flex-1 min-w-0 mr-4">
                      <div className="flex flex-wrap items-center gap-2 2xl:gap-4">
                        <div className="text-sm font-medium text-[#1F1F1F]">
                          <span>Tags: </span>
                        </div>
                        {screen.tags &&
                          screen.tags.slice(0, 5).map((tag, tagIndex) => (
                            <span
                              key={tagIndex}
                              className="inline-flex items-center px-2 py-1 rounded-[10px] text-xs 2xl:text-sm font-normal bg-[#F8F8F8] text-[#1F1F1F] border-[0.5px] border-[#808191]"
                            >
                              {tag.value} ({tag.tag})
                            </span>
                          ))}
                        {(!screen.tags || screen.tags.length === 0) && (
                          <span className="text-xs text-gray-400">No tags</span>
                        )}
                      </div>
                    </div>

                    <div className="w-8 flex-shrink-0">
                      <button
                        onClick={() => handleEditScreen(screen)}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                      >
                        <PenIcon className="w-4 h-4 3xl:w-6 3xl:h-6" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ScreensListComponent: React.FC = () => {
  const { screens, deleteScreen, addScreen, updateScreen } = useScreens();
  const { groups, addGroup, removeGroup } = useGroups();
  const { availableTags, addTag, updateTag, deleteTag, setAvailableTags } =
    useTags();
  const navigate = useNavigate();
  const { showToast, toasts } = useToast();
  const { setTitle, setShowBackButton, pushNavState } = useTopBar();
  const location = useLocation();

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<SortType>("name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [selectedScreens, setSelectedScreens] = useState<Set<string>>(
    new Set()
  );
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [newlyCreatedGroupId, setNewlyCreatedGroupId] = useState<string | null>(
    null
  );
  const [filters, setFilters] = useState<FilterState>({
    status: [],
    groups: [],
    orientations: [],
    tags: [],
    cities: [],
  });
  const [showScreenUploadPopup, setShowScreenUploadPopup] = useState(false);
  const [showTagManagementPopup, setShowTagManagementPopup] = useState(false);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [showAddGroupPopup, setShowAddGroupPopup] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingScreen, setEditingScreen] = useState<Screen | null>(null);

  useEffect(() => {
    const extractedTags = new Map<string, Tag>();
    screens.forEach((screen: Screen) => {
      if (screen.tags) {
        screen.tags.forEach((tag) => {
          if (!extractedTags.has(tag.tag.toLowerCase())) {
            extractedTags.set(tag.tag.toLowerCase(), {
              id: `extracted-${tag.tag.toLowerCase().replace(/\s+/g, "-")}`,
              name: tag.tag,
            });
          }
        });
      }
    });

    setAvailableTags((currentTags) => {
      const existingTagNames = new Set(
        currentTags.map((tag) => tag.name.toLowerCase())
      );
      const newTags = Array.from(extractedTags.values()).filter(
        (tag) => !existingTagNames.has(tag.name.toLowerCase())
      );
      return [...currentTags, ...newTags];
    });
  }, [screens, setAvailableTags]);

  useEffect(() => {
    const title = "Screens";
    const showBack = false;
    setTitle(title);
    setShowBackButton(showBack);
    pushNavState({
      path: location.pathname,
      title,
      showBackButton: showBack,
    });
  }, [location.pathname, setTitle, setShowBackButton, pushNavState]);

  // Handle newly created group from navigation state
  useEffect(() => {
    if (location.state?.newlyCreatedGroupId) {
      setNewlyCreatedGroupId(location.state.newlyCreatedGroupId);
      const timer = setTimeout(() => {
        setNewlyCreatedGroupId(null);
        navigate("/screens/list", { state: {}, replace: true });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [location.state, navigate]);

  const filteredAndSortedScreens = useMemo(() => {
    let filtered = screens.filter((screen: Screen) => {
      const matchesSearch =
        screen.name?.toLowerCase().includes(search.toLowerCase()) ||
        (screen.tags &&
          screen.tags.some(
            (tag) =>
              tag.tag.toLowerCase().includes(search.toLowerCase()) ||
              tag.value.toLowerCase().includes(search.toLowerCase())
          ));

      if (!matchesSearch) return false;

      if (filters.status.length > 0) {
        if (!filters.status.includes(screen.status.toLowerCase())) {
          return false;
        }
      }

      if (filters.groups.length > 0) {
        if (!filters.groups.includes(screen.group || "")) {
          return false;
        }
      }

      if (filters.orientations.length > 0) {
        if (!filters.orientations.includes(screen.orientation)) {
          return false;
        }
      }

      if (filters.tags.length > 0) {
        const screenTagValues = screen.tags?.map((tag) => tag.value) || [];
        const hasMatchingTag = filters.tags.some((filterTag) =>
          screenTagValues.includes(filterTag)
        );
        if (!hasMatchingTag) {
          return false;
        }
      }

      if (filters.cities.length > 0) {
        const screenCities =
          screen.tags
            ?.filter((tag) => tag.tag.toLowerCase() === "city")
            .map((tag) => tag.value) || [];
        if (screenCities.length > 0) {
          const hasMatchingCity = filters.cities.some((filterCity) =>
            screenCities.includes(filterCity)
          );
          if (!hasMatchingCity) {
            return false;
          }
        } else if (filters.cities.length > 0) {
          return false;
        }
      }

      return true;
    });

    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      switch (sortBy) {
        case "name":
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case "group":
          aValue = groups.find((g) => g.id === a.group)?.name || "";
          bValue = groups.find((g) => g.id === b.group)?.name || "";
          break;
        case "status":
          aValue = a.status;
          bValue = b.status;
          break;
        case "createdAt":
          aValue = a.createdAt;
          bValue = b.createdAt;
          break;
        default:
          return 0;
      }
      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [screens, search, sortBy, sortOrder, filters, groups]);

  const { groupedScreens, ungroupedScreens } = useMemo(() => {
    const grouped = filteredAndSortedScreens.filter((screen) => screen.group);
    const ungrouped = filteredAndSortedScreens.filter(
      (screen) => !screen.group
    );
    return { groupedScreens: grouped, ungroupedScreens: ungrouped };
  }, [filteredAndSortedScreens]);

  const totalPages = Math.ceil(ungroupedScreens.length / itemsPerPage);
  const paginatedUngroupedScreens = ungroupedScreens.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, search]);

  const showToastWrapper = (type: string, title: string, message: string) => {
    if (showToast) {
      showToast(type as ToastType, title, message);
    }
  };

  const handleApplyFilters = (newFilters: FilterState) => {
    setFilters(newFilters);
    setShowFilterPopup(false);
    if (showToast) {
      showToast(
        "success",
        "Filters Applied",
        "Screen filters have been applied successfully"
      );
    }
  };

  const handleOpenFilter = () => {
    setShowFilterPopup(true);
  };

  const getActiveFiltersCount = () => {
    return (
      filters.status.length +
      filters.groups.length +
      filters.orientations.length +
      filters.tags.length +
      filters.cities.length
    );
  };

  const handleAddGroup = () => {
    setShowAddGroupPopup(true);
  };

  const handleSaveGroup = (groupName: string): boolean => {
    const success = addGroup(groupName);
    if (success) {
      const newGroup = groups.find((g) => g.name === groupName);
      if (newGroup) {
        setNewlyCreatedGroupId(newGroup.id);
        setTimeout(() => setNewlyCreatedGroupId(null), 3000);
      }
      if (showToast) {
        showToast(
          "success",
          "Group Added",
          "Group has been added successfully"
        );
      }
    }
    return success;
  };

  const handleDeleteGroup = (groupId: string) => {
    removeGroup(groupId);
    screens.forEach((screen) => {
      if (screen.group === groupId) {
        updateScreen(screen.id, { group: "" });
      }
    });
    if (showToast) {
      showToast(
        "success",
        "Group Deleted",
        "Group has been deleted successfully"
      );
    }
  };

  const handleUploadScreen = (screenData: {
    name: string;
    group: string;
    orientation: string;
    tags: SelectedTag[];
  }) => {
    if (isEditMode && editingScreen) {
      const updatedScreen = {
        ...editingScreen,
        name: screenData.name,
        group: screenData.group,
        orientation: screenData.orientation,
        tags: screenData.tags,
      };
      updateScreen(editingScreen.id, updatedScreen);
      if (showToast) {
        showToast(
          "success",
          "Screen Updated",
          "Screen has been updated successfully"
        );
      }
      setIsEditMode(false);
      setEditingScreen(null);
    } else {
      const newScreen = {
        name: screenData.name,
        group: screenData.group,
        orientation: screenData.orientation,
        tags: screenData.tags,
        status: "offline",
      };
      addScreen(newScreen);
      if (showToast) {
        showToast(
          "success",
          "Screen Added",
          "Screen has been added successfully"
        );
      }
    }
    setShowScreenUploadPopup(false);
  };

  const handleAddScreen = () => {
    setIsEditMode(false);
    setEditingScreen(null);
    setShowScreenUploadPopup(true);
  };

  const handleEditScreen = (screen: Screen) => {
    setIsEditMode(true);
    setEditingScreen(screen);
    setShowScreenUploadPopup(true);
  };

  const handleClosePopup = () => {
    setShowScreenUploadPopup(false);
    setIsEditMode(false);
    setEditingScreen(null);
  };

  const handleUpdateTags = () => {
    setShowTagManagementPopup(true);
  };

  const handleSort = (newSortBy: SortType) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(newSortBy);
      setSortOrder("asc");
    }
  };

  const handleSelectScreen = (screenId: string) => {
    const newSelected = new Set(selectedScreens);
    if (newSelected.has(screenId)) {
      newSelected.delete(screenId);
    } else {
      newSelected.add(screenId);
    }
    setSelectedScreens(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedScreens.size === paginatedUngroupedScreens.length) {
      setSelectedScreens(new Set());
    } else {
      setSelectedScreens(new Set(paginatedUngroupedScreens.map((s) => s.id)));
    }
  };

  const handleDeleteScreen = (screenId: string) => {
    deleteScreen(screenId);
    if (showToast) {
      showToast(
        "success",
        "",
        "Screen deleted successfully!",
        `delete-${screenId}`
      );
    }
    setSelectedScreens((prev) => {
      const newSet = new Set(prev);
      newSet.delete(screenId);
      return newSet;
    });
  };

  const handleBulkDelete = () => {
    Array.from(selectedScreens).forEach((id) => deleteScreen(id));
    if (showToast) {
      showToast(
        "success",
        "",
        `${selectedScreens.size} screens deleted successfully!`,
        `bulk-delete-${Date.now()}`
      );
    }
    setSelectedScreens(new Set());
  };

  const handleDuplicateScreen = (screen: Screen) => {
    if (showToast) {
      showToast(
        "info",
        "",
        "Duplicate functionality coming soon!",
        `duplicate-${screen.id}`
      );
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "live":
        return "bg-green-100 text-green-700";
      case "offline":
        return "bg-red-100 text-red-700";
      case "maintenance":
        return "bg-yellow-100 text-yellow-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "live":
        return <WifiLiveIcon width={12} height={12} />;
      case "offline":
        return <WifiOfflineIcon width={12} height={12} />;
      case "maintenance":
        return "⚠";
      default:
        return null;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeDropdown) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [activeDropdown]);

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-white p-6 mt-1">
        <div className="max-w-full mx-auto">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <span className="text-gray-600">
                Total {filteredAndSortedScreens.length} screens
                {getActiveFiltersCount() > 0 && (
                  <span className="ml-2 text-blue-600">
                    ({getActiveFiltersCount()} filters applied)
                  </span>
                )}
              </span>
              {selectedScreens.size > 0 && (
                <span className="text-blue-600">
                  ({selectedScreens.size} selected)
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleAddGroup}
                className="flex items-center gap-2 px-4 py-2 text-[#0084A4] text-xs 2xl:text-base"
              >
                <AddGroup className="w-5 h-5 3xl:w-6 3xl:h-6" />
                Add new group
              </button>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 3xl:w-5 3xl:h-5" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search screen"
                  className="w-64 pl-10 pr-4 py-2 h-[32px] md:h-[32px] lg:h-[46px] xl:h-[42px] 2xl:h-[46px] 3xl:h-[52px] border border-gray-300 rounded-[10px] focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs 2xl:text-base"
                />
              </div>
              <button
                onClick={handleOpenFilter}
                className={`rounded-[10px] flex items-center justify-center relative
                            w-[32px] h-[32px] md:w-[32px] md:h-[32px]
                            xl:w-[42px] xl:h-[42px]
                            lg:w-[46px] lg:h-[46px]
                            2xl:w-[46px] 2xl:h-[46px]
                            3xl:w-[52px] 3xl:h-[52px] hover:bg-blue-50 transition-colors duration-200 ${
                              getActiveFiltersCount() > 0
                                ? "text-blue-600 bg-blue-50 border-blue-500"
                                : "text-blue-500 border-[#0091AF]"
                            }`}
                style={{ borderWidth: "0.5px" }}
              >
                <Filter className="w-5 h-5 lg:w-6 lg:h-6" />
                {getActiveFiltersCount() > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {getActiveFiltersCount()}
                  </span>
                )}
              </button>
              <button
                onClick={handleUpdateTags}
                className="inline-flex items-center gap-2 px-4 py-2 h-[32px] md:h-[32px] lg:h-[46px] xl:h-[42px] 2xl:h-[46px] 3xl:h-[52px] border border-[#0084A4] bg-[#F2FAFD] text-[#0084A4] rounded-[10px] text-xs 2xl:text-base font-medium"
              >
                <AddTag className="w-5 h-5 3xl:w-6 3xl:h-6" />
                Update tags
              </button>
              <button
                onClick={handleAddScreen}
                className="flex items-center gap-2 px-4 py-2 h-[32px] md:h-[32px] lg:h-[46px] xl:h-[42px] 2xl:h-[46px] 3xl:h-[52px] bg-[#0BA6C7] text-white rounded-[10px] hover:bg-[#0991A1] text-xs 2xl:text-base"
              >
                <ScreenAdd className="w-5 h-5 3xl:w-6 3xl:h-6" />
                Add screen
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="px-6 pb-24 2xl:pb-32">
        {groups.length === 0 && filteredAndSortedScreens.length === 0 ? (
          <div className="flex flex-col items-center bg-white rounded-lg p-12">
            <div className="text-gray-400 text-center">
              <div className="flex items-center justify-center">
                <UploadScreen className="w-52 h-52 -mb-8" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {search || getActiveFiltersCount() > 0
                  ? "No screens match your criteria"
                  : "No screens or groups found"}
              </h3>
              <p className="text-gray-500 mb-4">
                {search || getActiveFiltersCount() > 0
                  ? "Try adjusting your search terms or filters"
                  : "Get started by adding your first screen or group"}
              </p>
              {!search && getActiveFiltersCount() === 0 && (
                <button
                  onClick={handleAddScreen}
                  className="inline-flex items-center justify-center gap-2 bg-[#0091AF] hover:bg-[#007B8A] text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  <ScreenAdd className="w-4 h-4" />
                  Add screen
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map((group) => (
              <GroupFolder
                key={group.id}
                group={group}
                screens={groupedScreens}
                onDeleteGroup={handleDeleteGroup}
                handleEditScreen={handleEditScreen}
                isNewlyCreated={newlyCreatedGroupId === group.id}
              />
            ))}
            {ungroupedScreens.length > 0 && (
              <div className="space-y-4">
                {paginatedUngroupedScreens.map((screen) => (
                  <div
                    key={screen.id}
                    className="bg-white rounded-[10px] px-6 py-3 3xl:py-5 hover:shadow-xl"
                    style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }}
                  >
                    <div className="flex items-center">
                      <div className="w-12 flex-shrink-0">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: "#0BA6C71A" }}
                        >
                          {screen.orientation === "landscape" && (
                            <Landscape className="w-5 h-5 text-[#0BA6C7]" />
                          )}
                          {screen.orientation === "portrait" && (
                            <Portrait className="w-5 h-5 text-[#0BA6C7]" />
                          )}
                          {screen.orientation === "custom" && (
                            <All className="w-5 h-5 text-[#0BA6C7]" />
                          )}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {screen.name}
                        </div>
                        <div className="text-sm text-gray-500 truncate flex items-center gap-2">
                          <span className="text-xs">{screen.id}</span>
                          <span className="text-gray-300">|</span>
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                              screen.status
                            )}`}
                          >
                            {getStatusIcon(screen.status)} {screen.status}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0 mr-4">
                        <div className="flex flex-wrap items-center gap-2 2xl:gap-4">
                          <div className="text-sm font-medium text-[#1F1F1F]">
                            <span>Tags: </span>
                          </div>
                          {screen.tags &&
                            screen.tags.slice(0, 5).map((tag, tagIndex) => (
                              <span
                                key={tagIndex}
                                className="inline-flex items-center px-2 py-1 rounded-[10px] text-xs 2xl:text-sm font-normal bg-[#F8F8F8] text-[#1F1F1F] border-[0.5px] border-[#808191]"
                              >
                                {tag.value} ({tag.tag})
                              </span>
                            ))}
                          {(!screen.tags || screen.tags.length === 0) && (
                            <span className="text-xs text-gray-400">
                              No tags
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="w-8 flex-shrink-0">
                        <button
                          onClick={() => handleEditScreen(screen)}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                        >
                          <PenIcon className="w-4 h-4 3xl:w-6 3xl:h-6" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {filteredAndSortedScreens.length > 0 && totalPages > 1 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-10">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="text-sm text-[#]">
              Result {filteredAndSortedScreens.length}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-2xl 3xl:text-5xl text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed"
              >
                ‹
              </button>
              <div className="flex gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 3xl:w-10 3xl:h-10 text-sm 3xl:text-base rounded-[10px] ${
                        currentPage === pageNum
                          ? "bg-[#0BA6C7] text-white"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-2xl 3xl:text-5xl text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}
      {showScreenUploadPopup && (
        <ScreenUploadPopup
          onClose={handleClosePopup}
          onUpload={handleUploadScreen}
          showToast={showToastWrapper}
          isEditMode={isEditMode}
          editScreenData={editingScreen}
        />
      )}
      {showTagManagementPopup && (
        <TagManagementPopup
          onClose={() => setShowTagManagementPopup(false)}
          showToast={showToastWrapper}
        />
      )}
      {showFilterPopup && (
        <ScreenFilterPopup
          isOpen={showFilterPopup}
          onClose={() => setShowFilterPopup(false)}
          onApplyFilters={handleApplyFilters}
          screens={screens}
          currentFilters={filters}
        />
      )}
      {showAddGroupPopup && (
        <AddGroupPopup
          onClose={() => setShowAddGroupPopup(false)}
          onSave={handleSaveGroup}
        />
      )}
      <ToastContainer toasts={toasts} />
    </div>
  );
};

export default ScreensListComponent;
